import { Link, useParams, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowRight, ChevronRight, ExternalLink, Mail, Phone, MapPin, Clock, Users } from 'lucide-react';
import api from '@/lib/api';
import AdminHint from '@/components/editable/AdminHint';
import PageHero from '@/components/PageHero';
import { useSiteData, usePageContent } from '@/context/SiteDataContext';

const PR_JOTFORM = 'https://form.jotform.com/253227849004053';

export function DepartmentsList() {
  const { departments } = useSiteData();
  const c = usePageContent('departments');
  const list = departments || [];
  return (
    <div>
      <PageHero slug="departments" overline="Ministry" heading="Departments" text="Ministries working in unity to serve the saints and reach the lost." />
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {list.map(d => (
            <Link key={d.id} to={`/departments/${d.id}`} data-testid={`department-link-${d.id}`} className="card-tactile overflow-hidden group">
              <div className="aspect-[16/10] overflow-hidden">
                {d.image && <img src={d.image} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-royal font-bold group-hover:text-gold transition">{d.name}</h3>
                {d.tagline && <div className="text-xs text-gold tracking-widest uppercase mt-2">{d.tagline}</div>}
                <p className="text-charcoal-muted text-sm mt-3">{d.mission}</p>
                <div className="mt-4 text-sm text-royal font-semibold flex items-center gap-1">Explore <ChevronRight className="w-4 h-4" /></div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function DepartmentDetail() {
  const { id } = useParams();
  const { departments, leaders, gallery = [] } = useSiteData();
  const dept = (departments || []).find(d => d.id === id);
  const [events, setEvents] = useState([]);
  const [newsList, setNewsList] = useState([]);

  useEffect(() => {
    if (!dept) return;
    const matchesName = (val) => (val || '').toLowerCase().includes((dept.name || '').toLowerCase()) ||
                                 (val || '').toLowerCase().includes((dept.id || '').toLowerCase());
    api.get('/events').then(r => setEvents((r.data || []).filter(e => matchesName(e.department)))).catch(() => {});
    api.get('/news').then(r => setNewsList((r.data || []).filter(n => matchesName(n.category) || matchesName(n.department)))).catch(() => {});
  }, [dept]);

  // Women's Department has its own mini-site — redirect AFTER hooks per Rules of Hooks
  if (id === 'women' || id === 'womens') {
    return <Navigate to="/womens" replace />;
  }

  if (!dept) return <div className="py-24 text-center">Department not found. <Link to="/departments" className="text-royal underline">Back</Link></div>;

  const galleryTag = (dept.gallery_tag || dept.name || '').toLowerCase();
  const relatedPhotos = (gallery || []).filter(g =>
    ((g.tag || '') + ' ' + (g.caption || '')).toLowerCase().includes(galleryTag)
  ).slice(0, 8);
  const values = (dept.values || '').split('\n').map(s => s.trim()).filter(Boolean);
  const isPr = dept.id === 'pr' || dept.name?.toLowerCase().includes('public relation');

  // ---- Build ordered leadership from `assignments` (new model) ----
  // Falls back to legacy `department_id` linkage when no assignments exist yet
  // so existing content keeps working during the rollout.
  const assignments = (dept.assignments || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const leadersById = Object.fromEntries((leaders || []).map(l => [l.id, l]));
  const assigned = assignments
    .map(a => ({ ...leadersById[a.leader_id], _position: a.position }))
    .filter(x => x && x.id);
  const legacyCabinet = (leaders || []).filter(l => l.department_id === dept.id);
  const cabinet = assigned.length ? assigned : legacyCabinet.map(l => ({ ...l, _position: l.department_role || l.role }));

  // Group by position (President → VP → Cabinet → Custom) while preserving admin-ordered sequence.
  const POSITION_RANK = { 'president': 0, 'vice president': 1, 'cabinet': 2 };
  const rank = (p) => POSITION_RANK[(p || '').toLowerCase()] ?? 3;
  const orderedCabinet = cabinet.slice().sort((a, b) => rank(a._position) - rank(b._position));

  // Lead officer = President assignment if present, otherwise legacy `dept.leader` string.
  const presidentAssignment = orderedCabinet.find(l => (l._position || '').toLowerCase() === 'president');
  const leadName = presidentAssignment?.name || dept.leader || 'TBD';
  const leadRole = presidentAssignment?._position || 'Lead Officer';

  return (
    <div>
      {/* HERO */}
      <section className="relative py-28 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          {dept.image && <img src={dept.image} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-br from-royal-dark/85 via-royal/70 to-royal-dark/90" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 text-white flex items-center gap-6 flex-wrap">
          {dept.logo && (
            <img src={dept.logo} alt="" className="w-24 h-24 rounded-full ring-4 ring-gold bg-white object-cover shrink-0" />
          )}
          <div className="min-w-0">
            <div className="overline text-gold mb-3">Department</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold">{dept.name}</h1>
            {dept.tagline && <p className="mt-3 text-xl text-white/85">{dept.tagline}</p>}
            {dept.motto && <p className="mt-2 text-gold italic font-serif text-lg">"{dept.motto}"</p>}
          </div>
        </div>
      </section>

      {/* ABOUT / MISSION */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8 space-y-6">
            {dept.description && (
              <div>
                <div className="overline mb-2">About</div>
                <p className="text-charcoal-muted text-lg leading-relaxed whitespace-pre-wrap">{dept.description}</p>
              </div>
            )}
            {dept.mission && (
              <div className="bg-royal/5 border-l-4 border-gold p-5 rounded">
                <div className="overline mb-1">Mission</div>
                <p className="text-royal font-serif text-lg">{dept.mission}</p>
              </div>
            )}
            {dept.vision && (
              <div className="bg-royal/5 border-l-4 border-gold p-5 rounded">
                <div className="overline mb-1">Vision</div>
                <p className="text-royal font-serif text-lg">{dept.vision}</p>
              </div>
            )}
            {values.length > 0 && (
              <div>
                <div className="overline mb-2">Core Values</div>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {values.map((v, i) => (
                    <li key={`${dept.id}-v-${i}`} className="flex items-start gap-2 text-charcoal-muted">
                      <span className="text-gold mt-1">◆</span> <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <aside className="md:col-span-4 space-y-4">
            <div className="bg-royal text-white p-6 rounded-lg">
              <div className="overline text-gold mb-3">{leadRole}</div>
              <div className="font-serif text-xl font-bold">{leadName}</div>
              <div className="mt-5 space-y-2 text-xs text-white/85">
                {dept.contact_email && <div><Mail className="inline w-4 h-4 mr-2 text-gold" /><a href={`mailto:${dept.contact_email}`} className="hover:text-gold underline">{dept.contact_email}</a></div>}
                {dept.contact_phone && <div><Phone className="inline w-4 h-4 mr-2 text-gold" /> {dept.contact_phone}</div>}
                {dept.meeting_time && <div><Clock className="inline w-4 h-4 mr-2 text-gold" /> {dept.meeting_time}</div>}
                {dept.meeting_location && <div><MapPin className="inline w-4 h-4 mr-2 text-gold" /> {dept.meeting_location}</div>}
              </div>
              <Link to="/contact" className="mt-5 btn-gold w-full justify-center">Contact Us <ArrowRight className="w-4 h-4" /></Link>
              {isPr && (
                <a href={PR_JOTFORM} target="_blank" rel="noreferrer" data-testid="pr-inquiry-btn"
                   className="mt-3 btn-outline-white w-full justify-center text-sm">
                  Submit PR Inquiry <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* LEADERSHIP CABINET */}
      {orderedCabinet.length > 0 && (
        <section className="bg-[#F8F9FA] py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="overline mb-3">The Leadership</div>
            <h2 className="font-serif text-3xl text-royal font-bold mb-8 gold-underline">{dept.name} Cabinet</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {orderedCabinet.map(l => (
                <div key={l.id} data-testid={`dept-cabinet-${l.id}`} className="card-tactile overflow-hidden">
                  <div className="aspect-square overflow-hidden" style={{ backgroundColor: '#F1E9D6' }}>
                    {l.photo && <img src={l.photo} alt={l.name} className="w-full h-full object-cover"
                      style={{ objectPosition: l.photoPosition || 'center top', transform: l.photoScale ? `scale(${l.photoScale})` : undefined, transformOrigin: 'center top' }} />}
                  </div>
                  <div className="p-4">
                    <div className="text-xs text-gold uppercase tracking-widest">{l._position || l.role}</div>
                    <div className="font-serif text-lg text-royal font-bold mt-1">{l.name}</div>
                    {l.role && l.role !== l._position && <div className="text-xs text-charcoal-muted mt-0.5">{l.role}</div>}
                    {l.bio && <div className="text-xs text-charcoal-muted mt-2 line-clamp-3">{l.bio}</div>}
                    {(l.contact_email || l.org_email) && (
                      <a href={`mailto:${l.contact_email || l.org_email}`} className="mt-2 inline-flex items-center gap-1 text-xs text-royal hover:text-gold">
                        <Mail className="w-3 h-3" /> {l.contact_email || l.org_email}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <AdminHint className="mt-4 text-xs text-charcoal-muted">Manage this cabinet from Admin ▸ Departments ▸ Edit ▸ <em>Leadership</em>.</AdminHint>
          </div>
        </section>
      )}

      {/* EVENTS + NEWS grid */}
      {(events.length > 0 || newsList.length > 0) && (
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-8">
            <div>
              <div className="overline mb-2">On the Calendar</div>
              <h3 className="font-serif text-2xl text-royal font-bold mb-5">Upcoming Events</h3>
              {events.length === 0 && <div className="text-sm text-charcoal-muted">No upcoming events at this time.</div>}
              <div className="space-y-3">
                {events.slice(0, 4).map(e => (
                  <div key={e.id} className="card-tactile p-4 flex gap-4">
                    <div className="text-center bg-royal text-white rounded p-2 min-w-[56px]">
                      <div className="text-[10px] uppercase">{(e.date || '').slice(5, 7)}</div>
                      <div className="text-lg font-bold">{(e.date || '').slice(8, 10)}</div>
                    </div>
                    <div className="min-w-0">
                      <div className="font-serif text-royal font-bold">{e.title}</div>
                      <div className="text-xs text-charcoal-muted">{e.time} · {e.location}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="overline mb-2">Announcements</div>
              <h3 className="font-serif text-2xl text-royal font-bold mb-5">Latest News</h3>
              {newsList.length === 0 && <div className="text-sm text-charcoal-muted">No announcements at this time.</div>}
              <div className="space-y-3">
                {newsList.slice(0, 4).map(n => (
                  <Link key={n.id} to={`/news/${n.id}`} className="block card-tactile p-4">
                    <div className="text-[10px] text-gold uppercase tracking-widest">{n.category || 'News'}</div>
                    <div className="font-serif text-royal font-bold">{n.title}</div>
                    {n.excerpt && <div className="text-xs text-charcoal-muted mt-1 line-clamp-2">{n.excerpt}</div>}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {relatedPhotos.length > 0 && (
        <section className="bg-[#F8F9FA] py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="overline mb-2">Gallery</div>
            <h3 className="font-serif text-2xl text-royal font-bold mb-6">Ministry Moments</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {relatedPhotos.map(p => (
                <div key={p.id} className="aspect-square overflow-hidden rounded"><img src={p.url} alt={p.caption || ''} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" /></div>
              ))}
            </div>
            <AdminHint className="mt-3 text-xs text-charcoal-muted">Tag gallery photos with "{galleryTag}" in Admin ▸ Gallery to feature them here.</AdminHint>
          </div>
        </section>
      )}

      {/* BACK / CONTACT footer strip */}
      <section className="bg-royal-dark text-white py-10">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4">
          <Link to="/departments" className="text-gold hover:text-white">‹ All departments</Link>
          <Link to="/contact" className="btn-outline-white">Contact the Jurisdiction</Link>
        </div>
      </section>
    </div>
  );
}
